import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";
import { FaUsers, FaUser, FaMapMarkerAlt, FaUtensils, FaBriefcase, FaHome, FaArrowUp } from "react-icons/fa";
import { MdCreditCard } from "react-icons/md";
import { BsChatDotsFill } from "react-icons/bs";

/* ===============================================================
   Colors
================================================================ */
const C = {
    blue: "#1f6fef",
    green: "#1f9d57",
    purple: "#7c3aed",
    orange: "#f59e0b",
    red: "#ef4444",
    cyan: "#06b6d4",
    border: "#e5eaf3",
    headBg: "#f3f6fb",
    text: "#1f2a44",
    muted: "#5b6b86",
    navy: "#14213d",
};

/* ===============================================================
   Static data (swap with API data later - the page logic stays)
================================================================ */
const ITEMS = [
    { name: "Food", color: C.blue, type: "dot" },
    { name: "Fee Payments", color: C.green, type: "dot" },
    { name: "Hostel", color: C.purple, type: "dot" },
    { name: "Placement", color: C.orange, type: "plus" },
    { name: "Distance", color: C.red, type: "pin" },
    { name: "Security", color: C.cyan, type: "ring" },
];
const ITEM_ICONS = [<FaUtensils />, <MdCreditCard />, <FaHome />, <FaBriefcase />, <FaMapMarkerAlt />, <FaMapMarkerAlt />];

// Totals shown when every filter is "All"
const BASE_QUERIES = [649, 543, 413, 337, 275, 243];
const BASE_RATES = [78.2, 74.6, 68.7, 63.4, 58.9, 52.7];

const REGIONS = [
    { id: "coastal", name: "Coastal Andhra", color: C.blue },
    { id: "rayalaseema", name: "Rayalaseema", color: C.green },
    { id: "palnadu", name: "Palnadu", color: C.purple },
];
const BASE_REGION_ROWS = [
    { id: "coastal", parents: 622, emp: 9, rate: 75.8, v: [78.4, 72.1, 69.2, 63.5, 58.7, 51.9] },
    { id: "rayalaseema", parents: 384, emp: 5, rate: 68.6, v: [71.2, 66.4, 62.8, 59.1, 54.6, 48.3] },
    { id: "palnadu", parents: 242, emp: 4, rate: 62.1, v: [67.5, 61.8, 58.4, 53.7, 49.3, 45.6] },
];
const BASE_REGION_TOTAL = { rate: 69.5, v: [72.4, 66.8, 63.5, 58.8, 54.2, 48.9] };

// mix = share of the district's queries per checklist item (Food ... Security)
const DISTRICTS = [
    { id: "tirupati", name: "Tirupati", region: "rayalaseema", parents: 420, emp: 6, completed: 320, pending: 100, rate: 78.2, mix: [0.32, 0.22, 0.15, 0.12, 0.1, 0.09] },
    { id: "nellore", name: "Nellore", region: "coastal", parents: 340, emp: 4, completed: 250, pending: 90, rate: 73.5, mix: [0.2, 0.3, 0.15, 0.14, 0.11, 0.1] },
    { id: "guntur", name: "Guntur", region: "coastal", parents: 310, emp: 4, completed: 220, pending: 90, rate: 68.9, mix: [0.18, 0.16, 0.14, 0.12, 0.28, 0.12] },
    { id: "palnadu", name: "Palnadu", region: "palnadu", parents: 280, emp: 2, completed: 190, pending: 90, rate: 62.4, mix: [0.16, 0.14, 0.3, 0.15, 0.13, 0.12] },
    { id: "anantapur", name: "Anantapur", region: "rayalaseema", parents: 230, emp: 2, completed: 150, pending: 80, rate: 56.8, mix: [0.14, 0.13, 0.12, 0.31, 0.16, 0.14] },
].map((d) => ({
    ...d,
    total: d.completed + d.pending,
    rates: [3, 1, -1, -3, -5, -7].map((o) => d.rate + o),
}));

const UNIVERSITIES = [
    { value: "all", label: "All Universities", share: 1 },
    { value: "au", label: "Andhra University", share: 0.4 },
    { value: "svu", label: "Sri Venkateswara University", share: 0.35 },
    { value: "anu", label: "Acharya Nagarjuna University", share: 0.25 },
];

const SERIES = [
    { key: "completed", label: "Completed", color: C.green },
    { key: "pending", label: "Pending", color: C.orange },
    { key: "total", label: "Total Queries", color: C.blue },
];

const PRESETS = [
    { label: "Last 7 Days", value: [dayjs().subtract(6, "day"), dayjs()] },
    { label: "Last 14 Days", value: [dayjs().subtract(13, "day"), dayjs()] },
    { label: "Last 30 Days", value: [dayjs().subtract(29, "day"), dayjs()] },
    { label: "This Month", value: [dayjs().startOf("month"), dayjs()] },
];

/* ===============================================================
   Data engine - everything on the page is derived from the filters
================================================================ */
const r1 = (n) => Math.round(n * 10) / 10;
const fmt = (n) => Math.round(n).toLocaleString("en-US");
const argmax = (arr) => arr.indexOf(Math.max(...arr));

const computeData = (region, district, uni, factor) => {
    const share = uni.share;
    const isAll = region === "all" && district === "all" && share === 1;
    const sel = DISTRICTS.filter(
        (d) => (region === "all" || d.region === region) && (district === "all" || d.id === district)
    );
    const sc = (n) => Math.round(n * share * factor);

    // district level rows (bar chart + details table)
    const districtRows = sel.map((d) => ({
        ...d,
        parentsS: sc(d.parents),
        empS: Math.max(1, Math.round(d.emp * share)),
        completedS: sc(d.completed),
        pendingS: sc(d.pending),
        totalS: sc(d.total),
        topIdx: argmax(d.mix),
    }));

    // checklist items (donut + checklist table)
    const items = ITEMS.map((it, i) => {
        if (isAll) return { ...it, queries: Math.round(BASE_QUERIES[i] * factor), rate: BASE_RATES[i] };
        const w = sel.reduce((s, d) => s + d.total * d.mix[i], 0);
        const q = w * share * factor;
        const rate = sel.reduce((s, d) => s + d.total * d.mix[i] * d.rates[i], 0) / w;
        return { ...it, queries: Math.round(q), rate };
    });
    const totalQ = items.reduce((s, i) => s + i.queries, 0) || 1;
    const pcts = items.map((i) => r1((i.queries / totalQ) * 100));
    pcts[pcts.length - 1] = r1(100 - pcts.slice(0, -1).reduce((s, p) => s + p, 0));

    // region summary table
    let regionRows;
    if (isAll) {
        regionRows = BASE_REGION_ROWS.map((r) => ({
            ...r,
            ...REGIONS.find((x) => x.id === r.id),
            parents: Math.round(r.parents * factor),
        }));
    } else {
        regionRows = REGIONS.map((rg) => {
            const ds = districtRows.filter((d) => d.region === rg.id);
            if (!ds.length) return null;
            const wavg = (fn) => {
                const w = ds.reduce((s, d) => s + d.total, 0);
                return ds.reduce((s, d) => s + fn(d) * d.total, 0) / w;
            };
            return {
                ...rg,
                parents: ds.reduce((s, d) => s + d.parentsS, 0),
                emp: ds.reduce((s, d) => s + d.empS, 0),
                rate: wavg((d) => d.rate),
                v: ITEMS.map((_, i) => wavg((d) => d.rates[i])),
            };
        }).filter(Boolean);
    }
    const rp = regionRows.reduce((s, r) => s + r.parents, 0) || 1;
    const regionTotal = {
        parents: rp,
        emp: regionRows.reduce((s, r) => s + r.emp, 0),
        rate: isAll ? BASE_REGION_TOTAL.rate : regionRows.reduce((s, r) => s + r.rate * r.parents, 0) / rp,
        v: isAll
            ? BASE_REGION_TOTAL.v
            : ITEMS.map((_, i) => regionRows.reduce((s, r) => s + r.v[i] * r.parents, 0) / rp),
    };

    const kpi = {
        parents: isAll ? Math.round(1248 * factor) : districtRows.reduce((s, d) => s + d.parentsS, 0),
        emp: isAll ? 18 : districtRows.reduce((s, d) => s + d.empS, 0),
        conv: totalQ,
    };

    return { items, pcts, totalQ, districtRows, regionRows, regionTotal, kpi };
};

/* ===============================================================
   Hooks
================================================================ */
const useCountUp = (target, ms = 600) => {
    const [val, setVal] = useState(target);
    const ref = useRef(target);
    useEffect(() => {
        const from = ref.current;
        const t0 = performance.now();
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - t0) / ms);
            const v = Math.round(from + (target - from) * (1 - Math.pow(1 - p, 3)));
            ref.current = v;
            setVal(v);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, ms]);
    return val;
};

/* ===============================================================
   Small reusable pieces
================================================================ */
const Card = ({ children, style }) => (
    <div className="pec-card" style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, ...style }}>
        {children}
    </div>
);

const Circle = ({ bg, size = 56, children, fontSize = 22 }) => (
    <div
        style={{
            width: size, height: size, borderRadius: "50%", background: bg, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize, flexShrink: 0,
        }}
    >
        {children}
    </div>
);

const StatCard = ({ bg, border, ring, iconBg, icon, label, value, change }) => {
    const shown = useCountUp(value);
    return (
        <div
            className="pec-stat"
            style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, display: "flex", alignItems: "center", gap: 16, padding: "0 20px" }}
        >
            <div style={{ width: 66, height: 66, borderRadius: "50%", background: ring, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Circle bg={iconBg} size={48}>{icon}</Circle>
            </div>
            <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: C.navy, lineHeight: 1.15 }}>{fmt(shown)}</div>
                <div style={{ fontSize: 13, color: C.muted, display: "flex", alignItems: "center", gap: 5 }}>
                    <FaArrowUp style={{ color: "#16a34a", fontSize: 11 }} />
                    <b style={{ color: "#16a34a" }}>{change}</b> vs last week
                </div>
            </div>
        </div>
    );
};

const ItemDot = ({ type, color }) => {
    const base = {
        width: 16, height: 16, borderRadius: "50%", background: color, display: "inline-flex",
        alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700, lineHeight: 1,
    };
    if (type === "pin") return <FaMapMarkerAlt style={{ color, fontSize: 16 }} />;
    if (type === "plus") return <span style={base}>+</span>;
    if (type === "ring")
        return (
            <span style={base}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />
            </span>
        );
    return <span style={base} />;
};

const SectionTitle = ({ children }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <FaMapMarkerAlt style={{ color: C.blue, fontSize: 18 }} />
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>{children}</h3>
    </div>
);

const Tip = ({ tip }) => {
    if (!tip) return null;
    const flip = tip.x > window.innerWidth - 260;
    return (
        <div
            style={{
                position: "fixed", zIndex: 5000, pointerEvents: "none",
                left: flip ? tip.x - 14 : tip.x + 14, top: tip.y + 14,
                transform: flip ? "translateX(-100%)" : "none",
                background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8,
                boxShadow: "0 8px 24px rgba(20,33,61,.18)", padding: "8px 12px", fontSize: 12.5, color: C.text, minWidth: 150,
            }}
        >
            {tip.node}
        </div>
    );
};

const TipRow = ({ color, label, value }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
        {color && <span style={{ width: 9, height: 9, borderRadius: "50%", background: color }} />}
        <span style={{ flex: 1 }}>{label}</span>
        <b>{value}</b>
    </div>
);

/* ===============================================================
   Donut chart (SVG) - hover a slice or a table row to highlight
================================================================ */
const DonutChart = ({ items, pcts, total, active, setActive, show, hide }) => {
    const size = 250, c = 125, R = 118, r = 62;
    const animatedTotal = useCountUp(total);
    const pt = (rad, ang) => [c + rad * Math.cos(ang), c + rad * Math.sin(ang)];
    let acc = -Math.PI / 2;

    const slices = items.map((it, i) => {
        const a0 = acc;
        const a1 = acc + (it.queries / total) * Math.PI * 2;
        acc = a1;
        const am = (a0 + a1) / 2;
        const [x0, y0] = pt(R, a0), [x1, y1] = pt(R, a1), [x2, y2] = pt(r, a1), [x3, y3] = pt(r, a0);
        const large = a1 - a0 > Math.PI ? 1 : 0;
        const path = `M${x0} ${y0} A${R} ${R} 0 ${large} 1 ${x1} ${y1} L${x2} ${y2} A${r} ${r} 0 ${large} 0 ${x3} ${y3}Z`;
        const [lx, ly] = pt((R + r) / 2 + 2, am);
        return { ...it, i, path, lx, ly, dx: Math.cos(am) * 6, dy: Math.sin(am) * 6 };
    });
    const act = active != null ? items[active] : null;

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
            {slices.map((s) => (
                <g
                    key={s.name}
                    style={{
                        transform: active === s.i ? `translate(${s.dx}px, ${s.dy}px)` : "none",
                        opacity: active != null && active !== s.i ? 0.55 : 1,
                        transition: "transform .2s ease, opacity .2s ease",
                        cursor: "pointer",
                    }}
                    onMouseEnter={() => setActive(s.i)}
                    onMouseMove={(e) =>
                        show(e, (
                            <>
                                <b style={{ color: s.color }}>{s.name}</b>
                                <TipRow label="Total Queries" value={fmt(s.queries)} />
                                <TipRow label="Share" value={`${pcts[s.i]}%`} />
                                <TipRow label="Completion Rate" value={`${s.rate.toFixed(1)}%`} />
                            </>
                        ))
                    }
                    onMouseLeave={() => { setActive(null); hide(); }}
                >
                    <path d={s.path} fill={s.color} stroke="#fff" strokeWidth="1.5" />
                    <text x={s.lx} y={s.ly} fill="#fff" fontSize="13" fontWeight="700" textAnchor="middle" dominantBaseline="middle" pointerEvents="none">
                        {pcts[s.i]}%
                    </text>
                </g>
            ))}
            <circle cx={c} cy={c} r={r - 1} fill="#fff" />
            {act ? (
                <>
                    <text x={c} y={c - 6} textAnchor="middle" fontSize="15" fontWeight="800" fill={act.color}>{act.name}</text>
                    <text x={c} y={c + 14} textAnchor="middle" fontSize="12.5" fill={C.muted}>{fmt(act.queries)} queries</text>
                </>
            ) : (
                <>
                    <text x={c} y={c - 4} textAnchor="middle" fontSize="20" fontWeight="800" fill={C.navy}>{fmt(animatedTotal)}</text>
                    <text x={c} y={c + 16} textAnchor="middle" fontSize="12.5" fill={C.muted}>Total Queries</text>
                </>
            )}
        </svg>
    );
};

/* ===============================================================
   Grouped bar chart (CSS) - hover a group for details, click to filter
================================================================ */
const BarChart = ({ rows, hidden, show, hide, onPick }) => {
    const H = 208;
    const maxV = Math.max(1, ...rows.map((d) => d.totalS));
    let top = Math.max(100, Math.ceil((maxV * 1.1) / 100) * 100);
    const step = top <= 500 ? 100 : Math.ceil(top / 500) * 100;
    top = Math.ceil(top / step) * step;
    const ticks = [];
    for (let t = top; t >= 0; t -= step) ticks.push(t);
    const px = (v) => (v / top) * H;

    return (
        <div style={{ display: "flex", marginTop: 6 }}>
            <div style={{ width: 40, position: "relative", height: H + 34 }}>
                {ticks.map((t) => (
                    <span key={t} style={{ position: "absolute", right: 8, top: H - px(t) + 6, fontSize: 12.5, color: C.text }}>{t}</span>
                ))}
            </div>

            <div style={{ flex: 1, position: "relative", height: H + 34 }}>
                {ticks.map((t) => (
                    <div
                        key={t}
                        style={{ position: "absolute", left: 0, right: 0, top: H - px(t) + 14, borderTop: t === 0 ? "1px solid #9aa5b8" : "1px solid #eef1f6" }}
                    />
                ))}
                <div style={{ position: "absolute", left: 0, top: 14, bottom: 34, borderLeft: "1px solid #9aa5b8" }} />

                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", paddingBottom: 20 }}>
                    {rows.map((d) => {
                        const vals = { completed: d.completedS, pending: d.pendingS, total: d.totalS };
                        return (
                            <div
                                key={d.id}
                                className="pec-group"
                                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", margin: "0 2px" }}
                                onClick={() => { hide(); onPick(d.id); }}
                                onMouseMove={(e) =>
                                    show(e, (
                                        <>
                                            <b>{d.name}</b>
                                            {SERIES.map((s) => <TipRow key={s.key} color={s.color} label={s.label} value={fmt(vals[s.key])} />)}
                                            <TipRow label="Completion" value={`${((d.completedS / (d.totalS || 1)) * 100).toFixed(1)}%`} />
                                            <div style={{ marginTop: 4, fontSize: 11, color: C.muted }}>Click to filter by district</div>
                                        </>
                                    ))
                                }
                                onMouseLeave={hide}
                            >
                                <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: H + 14 }}>
                                    {SERIES.map((s) => (
                                        <div
                                            key={s.key}
                                            style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", visibility: hidden[s.key] ? "hidden" : "visible" }}
                                        >
                                            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.navy, marginBottom: 3 }}>{vals[s.key]}</div>
                                            <div className="pec-bar" style={{ width: 36, height: px(vals[s.key]), background: s.color, borderRadius: "3px 3px 0 0" }} />
                                        </div>
                                    ))}
                                </div>
                                <div style={{ marginTop: 6, fontSize: 13, color: C.text }}>{d.name}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

/* ===============================================================
   Shared table styles
================================================================ */
const th = (extra) => ({
    background: C.headBg, color: C.text, fontWeight: 600, fontSize: 13, padding: "10px 12px",
    borderBottom: `1px solid ${C.border}`, textAlign: "center", ...extra,
});
const td = (extra) => ({
    padding: "9px 12px", fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}`, textAlign: "center", ...extra,
});

const css = `
  .pec-stat{transition:transform .2s ease, box-shadow .2s ease}
  .pec-stat:hover{transform:translateY(-3px);box-shadow:0 8px 20px rgba(31,111,239,.16)}
  .pec-card{transition:box-shadow .2s ease}
  .pec-card:hover{box-shadow:0 6px 18px rgba(20,33,61,.07)}
  .pec-row td{transition:background .15s ease}
  .pec-row:hover td,.pec-row.pec-active td{background:#eef4ff}
  .pec-click{cursor:pointer}
  .pec-group{transition:background .15s ease;border-radius:6px;cursor:pointer}
  .pec-group:hover{background:rgba(31,111,239,.07)}
  .pec-bar{transition:height .5s ease, filter .15s ease}
  .pec-bar:hover{filter:brightness(1.15)}
  .pec-legend{cursor:pointer;user-select:none;transition:opacity .15s ease}
  .pec-legend:hover{opacity:.7}
`;

/* ===============================================================
   Page
================================================================ */
const ParentEmployeeChecklist = () => {
    const [region, setRegion] = useState("all");
    const [district, setDistrict] = useState("all");
    const [university, setUniversity] = useState("all");
    const [range, setRange] = useState([dayjs("2026-10-08"), dayjs("2026-10-14")]);
    const [activeItem, setActiveItem] = useState(null);
    const [hidden, setHidden] = useState({});
    const [tip, setTip] = useState(null);

    const show = (e, node) => setTip({ x: e.clientX, y: e.clientY, node });
    const hide = () => setTip(null);

    const uni = UNIVERSITIES.find((u) => u.value === university);
    const days = Math.max(1, range[1].diff(range[0], "day") + 1);
    const factor = days / 7; // static data is a 7-day baseline

    const data = useMemo(() => computeData(region, district, uni, factor), [region, district, uni, factor]);
    const { items, pcts, totalQ, districtRows, regionRows, regionTotal, kpi } = data;

    const regionOptions = [{ value: "all", label: "All Regions" }, ...REGIONS.map((r) => ({ value: r.id, label: r.name }))];
    const districtOptions = [
        { value: "all", label: "All Districts" },
        ...DISTRICTS.filter((d) => region === "all" || d.region === region).map((d) => ({ value: d.id, label: d.name })),
    ];

    const onRegionChange = (v) => {
        setRegion(v);
        const d = DISTRICTS.find((x) => x.id === district);
        if (v !== "all" && d && d.region !== v) setDistrict("all");
    };
    const pickRegion = (id) => { setRegion(region === id ? "all" : id); setDistrict("all"); };
    const pickDistrict = (id) => setDistrict(district === id ? "all" : id);

    const filterLabel = { fontSize: 13.5, fontWeight: 700, color: C.navy, marginBottom: 8 };
    const selectStyle = { width: "100%", height: 38 };

    return (
        <div style={{ background: "#f7f9fd", margin: -24, padding: 4, fontFamily: "inherit" }}>
            <style>{css}</style>

            {/* ---------------- Page header ---------------- */}
            <Card style={{ padding: "14px 18px", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <div
                            style={{
                                width: 62, height: 62, borderRadius: "50%", background: "#e6eefc", color: C.blue,
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28,
                            }}
                        >
                            <FaUsers />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: 21, fontWeight: 800, color: C.navy }}>
                                District &amp; Region Based Parent–Employee Checklist
                            </h2>
                            <p style={{ margin: "2px 0 0", fontSize: 13.5, color: C.muted }}>
                                Overview of parent and employee checklist completion across Andhra Pradesh regions and districts.
                            </p>
                        </div>
                    </div>
                    <DatePicker.RangePicker
                        value={range}
                        onChange={(v) => v && v[0] && v[1] && setRange(v)}
                        presets={PRESETS}
                        format="DD-MM-YYYY"
                        allowClear={false}
                        style={{ width: 228, height: 38, fontWeight: 500 }}
                    />
                </div>

                {/* ---------------- Filters + stat cards ---------------- */}
                <div style={{ display: "grid", gridTemplateColumns: "2.6fr 1fr 1fr 1fr", gap: 12, marginTop: 14 }}>
                    <div
                        style={{
                            background: "#f1f5fc", border: `1px solid ${C.border}`, borderRadius: 10,
                            padding: "18px 14px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16,
                        }}
                    >
                        <div>
                            <div style={filterLabel}>Region</div>
                            <Select value={region} onChange={onRegionChange} options={regionOptions} style={selectStyle} />
                        </div>
                        <div>
                            <div style={filterLabel}>District</div>
                            <Select value={district} onChange={setDistrict} options={districtOptions} style={selectStyle} />
                        </div>
                        <div>
                            <div style={filterLabel}>University</div>
                            <Select value={university} onChange={setUniversity} options={UNIVERSITIES} style={selectStyle} />
                        </div>
                    </div>

                    <StatCard bg="#eaf1fd" border="#d3e2fb" ring="#d6e5fc" iconBg={C.blue} icon={<FaUsers />} label="Total Parents" value={kpi.parents} change="12%" />
                    <StatCard bg="#e9f7ef" border="#cdeedb" ring="#d1f0de" iconBg={C.green} icon={<FaUser />} label="Total Employees" value={kpi.emp} change="6%" />
                    <StatCard bg="#f1ecfc" border="#e1d6f8" ring="#e3d9f8" iconBg={C.purple} icon={<BsChatDotsFill />} label="Total Conversations" value={kpi.conv} change="18%" />
                </div>
            </Card>

            {/* ---------------- Charts row ---------------- */}
            <div style={{ display: "grid", gridTemplateColumns: "0.86fr 1fr", gap: 12, marginBottom: 12 }}>
                <Card style={{ padding: "16px 16px 14px" }}>
                    <h3 style={{ margin: "0 0 12px", fontSize: 17, fontWeight: 700, color: C.navy }}>Checklist Completion (Region Wise)</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <DonutChart items={items} pcts={pcts} total={totalQ} active={activeItem} setActive={setActiveItem} show={show} hide={hide} />
                        <div style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={th({ textAlign: "left" })}>Checklist Item</th>
                                        <th style={th()}>Total Queries</th>
                                        <th style={th()}>Completion Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it, i) => (
                                        <tr
                                            key={it.name}
                                            className={"pec-row" + (activeItem === i ? " pec-active" : "")}
                                            onMouseEnter={() => setActiveItem(i)}
                                            onMouseLeave={() => setActiveItem(null)}
                                        >
                                            <td style={td({ textAlign: "left" })}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                                                    <ItemDot type={it.type} color={it.color} />
                                                    {it.name}
                                                </span>
                                            </td>
                                            <td style={td()}>{fmt(it.queries)}</td>
                                            <td style={td()}>{it.rate.toFixed(1)}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Card>

                <Card style={{ padding: "16px 18px 10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>District Wise Checklist Completion</h3>
                        <div style={{ display: "flex", gap: 18 }}>
                            {SERIES.map((s) => (
                                <span
                                    key={s.key}
                                    className="pec-legend"
                                    title="Click to show / hide"
                                    onClick={() => setHidden((h) => ({ ...h, [s.key]: !h[s.key] }))}
                                    style={{
                                        display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text,
                                        opacity: hidden[s.key] ? 0.4 : 1, textDecoration: hidden[s.key] ? "line-through" : "none",
                                    }}
                                >
                                    <span style={{ width: 12, height: 12, borderRadius: "50%", background: s.color }} />
                                    {s.label}
                                </span>
                            ))}
                        </div>
                    </div>
                    <BarChart rows={districtRows} hidden={hidden} show={show} hide={hide} onPick={pickDistrict} />
                </Card>
            </div>

            {/* ---------------- Tables row ---------------- */}
            <div style={{ display: "grid", gridTemplateColumns: "1.22fr 1fr", gap: 12 }}>
                {/* Region Wise Checklist Summary */}
                <Card style={{ padding: "14px 14px 12px" }}>
                    <SectionTitle>Region Wise Checklist Summary</SectionTitle>
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, overflow: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={th({ textAlign: "left", fontSize: 12 })}>Region</th>
                                    <th style={th({ fontSize: 12 })}>Total<br />Parents</th>
                                    <th style={th({ fontSize: 12 })}>Total<br />Employees</th>
                                    <th style={th({ fontSize: 12 })}>Completion<br />Rate (%)</th>
                                    {ITEMS.map((it) => (
                                        <th key={it.name} style={th({ fontSize: 12, fontWeight: 500 })}>{it.name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {regionRows.map((r) => (
                                    <tr
                                        key={r.id}
                                        className={"pec-row pec-click" + (region === r.id ? " pec-active" : "")}
                                        title="Click to filter by region"
                                        onClick={() => pickRegion(r.id)}
                                    >
                                        <td style={td({ textAlign: "left", fontSize: 12 })}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                                <span style={{ width: 16, height: 16, borderRadius: "50%", background: r.color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FaMapMarkerAlt style={{ color: "#fff", fontSize: 8 }} />
                                                </span>
                                                {r.name}
                                            </span>
                                        </td>
                                        <td style={td({ fontSize: 12 })}>{fmt(r.parents)}</td>
                                        <td style={td({ fontSize: 12 })}>{r.emp}</td>
                                        <td style={td({ fontSize: 12 })}>{r.rate.toFixed(1)}</td>
                                        {r.v.map((v, i) => (
                                            <td key={i} style={td({ fontSize: 12 })}>{v.toFixed(1)}</td>
                                        ))}
                                    </tr>
                                ))}
                                <tr style={{ background: C.headBg }}>
                                    <td style={td({ textAlign: "left", fontWeight: 700, borderBottom: "none" })}>Total</td>
                                    <td style={td({ fontWeight: 700, borderBottom: "none" })}>{fmt(regionTotal.parents)}</td>
                                    <td style={td({ fontWeight: 700, borderBottom: "none" })}>{regionTotal.emp}</td>
                                    <td style={td({ fontWeight: 700, borderBottom: "none" })}>{regionTotal.rate.toFixed(1)}</td>
                                    {regionTotal.v.map((v, i) => (
                                        <td key={i} style={td({ fontWeight: 700, borderBottom: "none" })}>{v.toFixed(1)}</td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card>

                {/* District Wise Checklist Details */}
                <Card style={{ padding: "14px 14px 12px" }}>
                    <SectionTitle>District Wise Checklist Details</SectionTitle>
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, overflow: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={th({ textAlign: "left" })}>District</th>
                                    <th style={th()}>Total Parents</th>
                                    <th style={th()}>Total Employees</th>
                                    <th style={th()}>Completion Rate</th>
                                    <th style={th({ textAlign: "left" })}>Top Checklist Item</th>
                                </tr>
                            </thead>
                            <tbody>
                                {districtRows.map((d, i) => {
                                    const last = i === districtRows.length - 1;
                                    const b = last ? { borderBottom: "none" } : {};
                                    const high = d.rate >= 65;
                                    return (
                                        <tr
                                            key={d.id}
                                            className={"pec-row pec-click" + (district === d.id ? " pec-active" : "")}
                                            title="Click to filter by district"
                                            onClick={() => pickDistrict(d.id)}
                                        >
                                            <td style={td({ textAlign: "left", ...b })}>{d.name}</td>
                                            <td style={td(b)}>{fmt(d.parentsS)}</td>
                                            <td style={td(b)}>{d.empS}</td>
                                            <td style={td(b)}>
                                                <span
                                                    style={{
                                                        display: "inline-block", minWidth: 72, padding: "2px 10px", borderRadius: 999, fontWeight: 700, fontSize: 12.5,
                                                        background: high ? C.green : "#facc4b", color: high ? "#fff" : "#4a3a00",
                                                    }}
                                                >
                                                    {d.rate.toFixed(1)}%
                                                </span>
                                            </td>
                                            <td style={td({ textAlign: "left", ...b })}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                                                    <span style={{ color: ITEMS[d.topIdx].color, fontSize: 18, display: "inline-flex" }}>{ITEM_ICONS[d.topIdx]}</span>
                                                    {ITEMS[d.topIdx].name}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>

            <Tip tip={tip} />
        </div>
    );
};

export default ParentEmployeeChecklist;